import {mapApiError} from "../../shared/api/errors";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {i18n} from "../../shared/i18n";


export const APP_TEXTS_LOCALE_STORAGE_KEY = "sorpaAdminAppTextsLocale";
export const APP_TEXTS_DRAFT_STORAGE_PREFIX = "sorpaAdminAppTextsDraft:v1";

// The backend serves the texts endpoints to platform roles only. «Владелец» also holds
// content.view, so the role check must stay next to the action check.
const APP_TEXTS_ROLES = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);

export const KIND_LABELS = Object.freeze({
    get flat() { return i18n.t("sections.content.p34"); },
    get structured() { return i18n.t("sections.content.p27"); },
    get full_page() { return i18n.t("sections.content.p35"); },
});

// Top-level parts of the onboarding override document the backend accepts.
const ONBOARDING_PARTS = ["locale_data", "wallets", "payments", "partner_badges", "hints"];

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const canViewAppTexts = ({role, hasAction} = {}) =>
    APP_TEXTS_ROLES.has(role) && typeof hasAction === "function" && hasAction("content.view");

// Per-country truth is `can_edit` of the section response; the scope flag and the
// capability gate the tab before any section is loaded.
export const canEditAppTexts = ({scope, view, hasAction} = {}) =>
    scope?.can_edit === true
    && typeof hasAction === "function"
    && hasAction("content.manage")
    && (view === undefined || view === null || view.can_edit === true);

export const editorModeForKind = (kind) => {
    if (kind === "structured") return "json";
    if (kind === "full_page") return "page";
    return "flat";
};

export const normalizeText = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value).replace(/\r\n/g, "\n");
    return text.trim() ? text : "";
};

export const stableStringify = (value) => {
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    if (isPlainObject(value)) {
        return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
    }
    return JSON.stringify(value === undefined ? null : value);
};

export const prettyJson = (value) => JSON.stringify(value ?? {}, null, 2);

// Rows whose stored value the client cannot send back: a key that is no longer in the
// base («stale») or a value stored as something other than text («invalid»).
export const isLegacyRow = (row) => row?.status === "stale" || row?.status === "invalid";

// Draft = {key: text}. `null` marks a stored key the editor removes (legacy flat rows,
// page keys); "" means "no own wording" in a phrase section and "empty" on a page.
export const initialDraft = (view) => Object.fromEntries(
    (view?.rows || []).map((row) => [row.key, typeof row.override === "string" ? row.override : ""]),
);

// Page rows = the server rows plus keys that exist only in the draft (added via JSON).
export const pageRows = (rows = [], draft = {}) => {
    const known = new Set(rows.map((row) => row.key));
    const added = Object.entries(draft || {})
        .filter(([key, value]) => !known.has(key) && value !== null && value !== undefined)
        .map(([key]) => ({key, base: null, override: null, effective: null, status: "extra", draftOnly: true}));
    return [...rows, ...added];
};

const flatOverrideValue = (row, raw) => {
    if (raw === null || raw === undefined) return "";
    const value = normalizeText(raw);
    return value && value !== row.base ? value : "";
};

const pageValue = (raw) => (raw === null || raw === undefined ? null : normalizeText(raw));

// The section payload `values`. Phrase sections: only non-empty values that differ from
// the base (the complete desired set of this country's overrides). Pages: every key.
export const diffOverride = ({rows = [], draft = {}, mode = "flat"} = {}) => {
    const values = {};
    if (mode === "page") {
        pageRows(rows, draft).forEach((row) => {
            const value = pageValue(draft[row.key]);
            if (value !== null) values[row.key] = value;
        });
        return values;
    }
    rows.forEach((row) => {
        if (row.status === "stale") return;
        const raw = draft[row.key];
        // An untouched non-text value cannot be sent back; saving drops it.
        if (row.status === "invalid" && raw === (row.override ?? "")) return;
        const value = flatOverrideValue(row, raw);
        if (value) values[row.key] = value;
    });
    return values;
};

export const changedKeys = ({rows = [], draft = {}, mode = "flat"} = {}) => {
    const saved = Object.fromEntries(rows.map((row) => [row.key, typeof row.override === "string" ? row.override : ""]));
    if (mode === "page") {
        return pageRows(rows, draft)
            .filter((row) => {
                const savedValue = row.draftOnly ? null : pageValue(saved[row.key]);
                return pageValue(row.key in draft ? draft[row.key] : saved[row.key]) !== savedValue;
            })
            .map((row) => row.key);
    }
    return rows
        .filter((row) => {
            const current = row.key in draft ? draft[row.key] : saved[row.key];
            if (isLegacyRow(row) && current === saved[row.key]) return false;
            if (isLegacyRow(row)) return true;
            return flatOverrideValue(row, current) !== flatOverrideValue(row, saved[row.key]);
        })
        .map((row) => row.key);
};

export const isDirty = (args = {}) => changedKeys(args).length > 0;

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const MISSING = Symbol("missing");

export const pickKeys = (object = {}, keys = []) =>
    Object.fromEntries(keys.filter((key) => hasOwn(object, key)).map((key) => [key, object[key]]));

// What a draft of this section starts from: the saved table values, the saved document a
// JSON draft replaces (the override document, or the whole page), and their version.
export const savedDraftBase = (view) => {
    const rows = Array.isArray(view?.rows) ? view.rows : [];
    const savedValues = initialDraft(view);
    const savedDocument = editorModeForKind(view?.section?.kind) === "page"
        ? diffOverride({rows, draft: savedValues, mode: "page"})
        : (isPlainObject(view?.document?.override) ? view.document.override : {});
    return {version: view?.version ?? null, savedValues, savedDocument};
};

// A draft keeps ONLY the table keys the editor touched (`values`) or the JSON text
// (`text`), plus the version and saved values it started from. Saving sends
// `baseVersion`, so whatever changed on the server in the meantime answers 409 instead
// of being overwritten with values the editor never looked at.
export const startDraft = ({version = null, savedValues = {}, savedDocument = null} = {}) => ({
    baseVersion: version ?? null,
    baseValues: {...savedValues},
    baseDocument: stableStringify(savedDocument ?? {}),
    values: {},
});

// Table values shown and sent: the saved section with the touched keys on top.
export const draftValues = (savedValues = {}, draft = null) => ({...savedValues, ...(draft?.values || {})});

// Full table values → only the keys that differ from the saved ones.
export const deltaFromValues = (full = {}, saved = {}) => Object.fromEntries(
    Object.entries(full || {}).filter(([key, value]) => !hasOwn(saved, key) || saved[key] !== value),
);

// Moves a draft onto a newer saved section («Загрузить актуальную версию», restoring a
// stored draft). Keys the editor changed stay on top; keys it touched but left as they
// were follow the server again; keys changed both here and on the server are returned
// as `conflicts` (the editor's text wins until the admin looks). A JSON draft is one
// whole document: it is kept, and `documentChanged` says the server moved on under it.
export const rebaseDraft = ({draft, version = null, savedValues = {}, savedDocument = null}) => {
    if (!draft) return {draft: null, conflicts: [], documentChanged: false};
    const baseValues = draft.baseValues || {};
    const values = {};
    const conflicts = [];
    Object.entries(draft.values || {}).forEach(([key, value]) => {
        const before = hasOwn(baseValues, key) ? baseValues[key] : MISSING;
        if (value === before) return;
        values[key] = value;
        const now = hasOwn(savedValues, key) ? savedValues[key] : MISSING;
        if (now !== before && now !== value) conflicts.push(key);
    });
    const documentKey = stableStringify(savedDocument ?? {});
    const documentChanged = typeof draft.text === "string"
        && typeof draft.baseDocument === "string"
        && draft.baseDocument !== documentKey;
    return {
        draft: {...draft, baseVersion: version ?? null, baseValues: {...savedValues}, baseDocument: documentKey, values},
        conflicts,
        documentChanged,
    };
};

// sessionStorage form: the touched keys with the saved values they started from.
export const storableDraft = (draft, {kind, jsonActive, text}) => ({
    kind,
    view: jsonActive ? "json" : "table",
    baseVersion: draft?.baseVersion ?? null,
    ...(jsonActive
        ? {documentText: text, baseDocument: draft?.baseDocument}
        : {
            values: draft?.values || {},
            baseValues: pickKeys(draft?.baseValues || {}, Object.keys(draft?.values || {})),
        }),
});

export const draftFromStored = (stored, {savedValues = {}} = {}) => {
    const values = isPlainObject(stored?.values) ? stored.values : {};
    return {
        baseVersion: stored?.baseVersion ?? null,
        // Without stored base values a touched key counts as changed against the current text.
        baseValues: isPlainObject(stored?.baseValues) ? stored.baseValues : pickKeys(savedValues, Object.keys(values)),
        baseDocument: typeof stored?.baseDocument === "string" ? stored.baseDocument : undefined,
        values,
        ...(typeof stored?.documentText === "string" ? {text: stored.documentText} : {}),
    };
};

export const rebaseResult = ({draft, conflicts = [], documentChanged = false} = {}) => {
    if (conflicts.length) {
        return {
            kind: "warning",
            message: i18n.t("sections.content.p36", {v0: conflicts.join(", ")}),
        };
    }
    if (documentChanged) {
        return {
            kind: "warning",
            message: i18n.t("sections.content.p37"),
        };
    }
    return {kind: "info", message: draft ? i18n.t("sections.content.p38") : i18n.t("sections.content.p39")};
};

// react-query v5: a query that cannot start because the browser is offline is pending
// with fetchStatus "paused" — neither isLoading nor isError.
export const queryWaitState = (query) => {
    if (!query?.isPending) return null;
    if (query.fetchStatus === "paused") return "offline";
    return query.fetchStatus === "fetching" ? "loading" : null;
};

export const OFFLINE_TITLE = i18n.t("sections.content.p40");
export const OFFLINE_COPY = i18n.t("sections.content.p41");

// The browser's JSON.parse message is English and engine-specific: keep only the
// position (when there is one) and say it in Russian.
const jsonErrorMessage = (error, source) => {
    const raw = String(error?.message || "");
    const position = /position (\d+)/.exec(raw);
    if (position) {
        const before = source.slice(0, Number(position[1])).split("\n");
        return i18n.t("sections.content.p42", {v0: before.length, v1: before[before.length - 1].length + 1});
    }
    if (/end of (json )?(input|data)/i.test(raw)) {
        return i18n.t("sections.content.p43");
    }
    return i18n.t("sections.content.p44");
};

export const parseJsonDraft = (text) => {
    const source = String(text ?? "");
    if (!source.trim()) return {value: {}, error: null};
    try {
        const value = JSON.parse(source);
        if (!isPlainObject(value)) return {value: null, error: i18n.t("sections.content.p45")};
        return {value, error: null};
    } catch (error) {
        return {value: null, error: jsonErrorMessage(error, source)};
    }
};

export const isJsonDraftDirty = (text, savedDocument) => {
    const parsed = parseJsonDraft(text);
    if (parsed.error) return true;
    return stableStringify(parsed.value) !== stableStringify(isPlainObject(savedDocument) ? savedDocument : {});
};

export const pageValuesError = (value) => {
    if (!isPlainObject(value)) return i18n.t("sections.content.p45");
    const wrong = Object.keys(value).filter((key) => typeof value[key] !== "string");
    return wrong.length ? i18n.t("sections.content.p46", {v0: wrong.join(", ")}) : null;
};

// JSON page → table draft: reference keys missing in the JSON become empty cells,
// server keys missing in the JSON are removed.
export const pageDraftFromJson = (rows = [], value = {}) => {
    const draft = {};
    rows.forEach((row) => {
        if (Object.prototype.hasOwnProperty.call(value, row.key)) draft[row.key] = value[row.key];
        else draft[row.key] = row.status === "extra" ? null : "";
    });
    Object.keys(value).forEach((key) => {
        if (!(key in draft)) draft[key] = value[key];
    });
    return draft;
};

export const pageDraftErrors = ({rows = [], draft = {}} = {}) => Object.fromEntries(
    pageRows(rows, draft)
        .filter((row) => draft[row.key] !== null && !normalizeText(row.key in draft ? draft[row.key] : ""))
        .map((row) => [row.key, [i18n.t("sections.content.p47")]]),
);

export const onboardingBaseDraft = (base) => {
    if (!isPlainObject(base)) return {};
    const parts = Object.fromEntries(ONBOARDING_PARTS.filter((part) => part in base).map((part) => [part, base[part]]));
    return Object.keys(parts).length ? parts : base;
};

export const rowFieldErrors = (fieldErrors = {}) => Object.entries(fieldErrors || {}).reduce((errors, [path, messages]) => {
    const match = /^values\.(.+)$/.exec(path);
    if (!match) return errors;
    const list = Array.isArray(messages) ? messages : [String(messages)];
    errors[match[1]] = [...(errors[match[1]] || []), ...list];
    return errors;
}, {});

export const otherFieldErrors = (fieldErrors = {}) => Object.entries(fieldErrors || {})
    .filter(([path]) => !/^values\./.test(path))
    .map(([path, messages]) => ({path, messages: Array.isArray(messages) ? messages : [String(messages)]}));

export const warningsByKey = (warnings = []) => (Array.isArray(warnings) ? warnings : []).reduce((groups, warning) => {
    const key = warning?.key;
    if (key === null || key === undefined || key === "") {
        groups.general.push(warning);
    } else {
        groups.byKey[key] = [...(groups.byKey[key] || []), warning];
    }
    return groups;
}, {byKey: {}, general: []});

export const defaultCountryId = (scope, headerScope) => {
    const countries = Array.isArray(scope?.countries) ? scope.countries : [];
    const find = (id) => countries.find((country) => String(country?.id) === String(id ?? ""));
    const header = String(headerScope ?? "").trim();
    if (/^[1-9]\d*$/.test(header) && find(header)) return find(header).id;
    const fallback = find(scope?.default_country_id);
    if (fallback) return fallback.id;
    return countries[0]?.id ?? null;
};

export const defaultLocale = (scope, storedLocale) => {
    const languages = Array.isArray(scope?.languages) ? scope.languages : [];
    if (storedLocale && languages.some((language) => language?.code === storedLocale)) return storedLocale;
    return languages[0]?.code ?? null;
};

export const localeLabel = (language) => {
    const code = String(language?.code ?? "");
    return language?.name ? `${language.name} (${code})` : code.toUpperCase();
};

export const pluralRu = (count, [one, few, many]) => {
    const value = Math.abs(Number(count) || 0);
    const lastTwo = value % 100;
    const last = value % 10;
    if (lastTwo >= 11 && lastTwo <= 14) return many;
    if (last === 1) return one;
    if (last >= 2 && last <= 4) return few;
    return many;
};

export const sectionCountsLabel = (section = {}) => {
    if (section.kind === "full_page") return section.has_override ? i18n.t("sections.content.p48") : i18n.t("sections.content.p49");
    const base = Number(section.base_key_count) || 0;
    const own = Number(section.override_key_count) || 0;
    const unit = section.kind === "structured"
        ? pluralRu(base, [i18n.t("sections.content.p50"), i18n.t("sections.content.p51"), i18n.t("sections.content.p52")])
        : pluralRu(base, [i18n.t("sections.content.p53"), i18n.t("sections.content.p54"), i18n.t("sections.content.p55")]);
    return i18n.t("sections.content.p56", {v0: base, v1: unit, v2: own});
};

export const formatShortDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("ru-RU", {day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"}).format(date);
};

export const updatedLabel = ({updated_at: updatedAt, updated_by: updatedBy} = {}) => {
    const date = formatShortDateTime(updatedAt);
    if (!date) return "";
    return updatedBy?.name ? i18n.t("sections.content.p57", {v0: date, v1: updatedBy.name}) : i18n.t("sections.content.p58", {v0: date});
};

export const overrideCount = (view) => {
    const kind = view?.section?.kind;
    if (kind === "structured") {
        const override = isPlainObject(view?.document?.override) ? view.document.override : {};
        const phrases = isPlainObject(override.locale_data) ? Object.keys(override.locale_data).length : 0;
        return phrases + Object.keys(override).filter((part) => part !== "locale_data").length;
    }
    const rows = Array.isArray(view?.rows) ? view.rows : [];
    if (kind === "full_page") return rows.filter((row) => row.status === "page" || row.status === "extra").length;
    return rows.filter((row) => row.status === "override").length;
};

export const saveResultMessage = (response) => {
    const status = response?.result?.status;
    if (status === "deleted") return i18n.t("sections.content.p59");
    if (status === "unchanged") return i18n.t("sections.content.p60");
    const warnings = Array.isArray(response?.warnings) ? response.warnings.length : 0;
    if (warnings) return i18n.t("sections.content.p61", {v0: warnings});
    return i18n.t("sections.content.p62", {v0: overrideCount(response)});
};

export const appTextsErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 403) return i18n.t("sections.content.p63");
    const serverMessage = error?.response?.data?.message;
    if (typeof serverMessage === "string" && serverMessage.trim()) return serverMessage.slice(0, 500);
    return mapApiError(error).message;
};

export const textareaRows = (...texts) => {
    const lines = Math.max(1, ...texts.map((text) => String(text ?? "").split("\n").length));
    return Math.min(6, lines);
};

export const matchesSearch = (row, draft, query) => {
    const needle = String(query ?? "").trim().toLocaleLowerCase("ru");
    if (!needle) return true;
    return [row.key, row.base, row.override, draft?.[row.key]]
        .some((value) => typeof value === "string" && value.toLocaleLowerCase("ru").includes(needle));
};

// Browser storage can be missing or throw (private mode, blocked site data).
const safeStorage = (name) => {
    try {
        return window[name] || null;
    } catch {
        return null;
    }
};

export const draftStorageKey = ({countryId, locale, section}) =>
    `${APP_TEXTS_DRAFT_STORAGE_PREFIX}:${countryId}:${locale}:${section}`;

export const readStoredDraft = (key) => {
    try {
        const raw = safeStorage("sessionStorage")?.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isPlainObject(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export const writeStoredDraft = (key, draft) => {
    try {
        const storage = safeStorage("sessionStorage");
        if (!storage) return false;
        storage.setItem(key, JSON.stringify({...draft, savedAt: new Date().toISOString()}));
        return true;
    } catch {
        return false;
    }
};

export const clearStoredDraft = (key) => {
    try {
        safeStorage("sessionStorage")?.removeItem(key);
    } catch {
        // Nothing to clean up when storage is unavailable.
    }
};

export const readStoredLocale = () => {
    try {
        return safeStorage("localStorage")?.getItem(APP_TEXTS_LOCALE_STORAGE_KEY) || null;
    } catch {
        return null;
    }
};

export const writeStoredLocale = (locale) => {
    try {
        safeStorage("localStorage")?.setItem(APP_TEXTS_LOCALE_STORAGE_KEY, locale);
    } catch {
        // The language choice is a convenience; losing it is fine.
    }
};
